'use client'
import Image from 'next/image'
import React, { useCallback, useMemo } from 'react'

import { trackEvent } from '@/utils/analytics'
import { cn } from '@/utils/twMerge'
import { asLink } from '@prismicio/client'
import { usePathname } from 'next/navigation'
import { PrismicRichText } from '@prismicio/react'
import { PrismicNextImage } from '@prismicio/next'

type DataObject = Record<string, any>

const Ebook = ({ data }: { data: DataObject }) => {
  const pathname = usePathname()

  const downloadLink = data.download_link
  const ctaTitle = data.cta_title?.[0]?.text ?? ''
  const ctaContent = data.cta_content?.[0]?.text ?? ''

  // Extract URL string from Prismic link field (handles all link types)
  const destUrl = useMemo(() => {
    if (!downloadLink) return null

    // asLink properly handles all Prismic link types (Web, Document, Media)
    const url = asLink(downloadLink)
    return url || downloadLink.url || null
  }, [downloadLink])

  const ebookData = useMemo(
    () => ({
      title: ctaTitle,
      content: ctaContent,
      srcPath: `https://secureframe.com${pathname}`,
      destPath: destUrl || '',
    }),
    [ctaTitle, ctaContent, pathname, destUrl],
  )

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()

      if (!destUrl) return

      trackEvent('Recommended Reading Clicked', ebookData)

      // Use window.location for external URLs, or open in new tab for downloads
      if (destUrl.startsWith('http')) {
        window.open(destUrl, '_blank', 'noopener,noreferrer')
      } else {
        window.location.href = destUrl
      }
    },
    [destUrl, ebookData],
  )

  return (
    <div className="my-8 grid grid-cols-1 gap-6 rounded-xl bg-primary-ink-100 px-8 py-12 md:grid-cols-5 md:gap-x-6">
      <div className="col-span-1 [&>img]:!m-0">
        <PrismicNextImage field={data.cta_logo} width={140} height={200} loading="eager" fallbackAlt="" />
      </div>
      <div className="col-span-1 max-w-xl md:col-span-4 [&>h2]:m-0 [&>h2]:text-3xl [&>h2]:tracking-wider [&>h2]:text-white [&>p]:mb-6 [&>p]:mt-4 [&>p]:text-white">
        <PrismicRichText field={data.cta_title} />
        <PrismicRichText field={data.cta_content} />

        <button
          className={cn('btn btn-secondary btn-default btn-interaction !w-fit', !data.cta_content[0]?.text && 'mt-6')}
          onClick={handleClick}>
          {data.cta_button_text}
          <Image src="/images/angle-right-black.svg" width={20} height={16} alt="" aria-hidden="true" />
        </button>
      </div>
    </div>
  )
}

export default Ebook
